import { MbscEventcalendarView } from '@mobiscroll/react';
import useQueryParams from './useQueryParams';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { getDayNumber } from 'shared/utils/dates';

type CalendarView = 'week' | 'day' | 'workweek' | 'month';

/**
 * Builds a Mobiscroll {@link MbscEventcalendarView} config object for the given
 * calendar view type and first-day-of-week setting.
 *
 * @param view - The calendar view type (`'week'`, `'day'`, `'workweek'`, `'month'`).
 * @param firstDayOfWeek - ISO day number for the first day of the week (0 = Sunday, 1 = Monday, etc.). Defaults to `1`.
 * @returns A Mobiscroll view configuration object.
 */
export const getCalendarConfig = (
	view: CalendarView,
	firstDayOfWeek: number = 1
): MbscEventcalendarView => {
	switch (view) {
		case 'month': {
			return {
				calendar: {
					type: 'month',
				},
			};
		}
		case 'day': {
			return {
				timeline: {
					type: 'month',
					timeCellStep: 60,
					timeLabelStep: 60,
					rowHeight: 'variable',
				},
			};
		}
		case 'workweek': {
			return {
				timeline: {
					type: 'month',
					eventList: false, // Disable event list to show time-based events
					startDay: firstDayOfWeek,
					endDay: firstDayOfWeek - 1 || 0,
					timeCellStep: 240,
					timeLabelStep: 240,
					rowHeight: 'variable',
				},
			};
		}
		case 'week':
		default: {
			return {
				timeline: {
					type: 'week',
					eventList: false, // Disable event list to show time-based events
					startDay: firstDayOfWeek,
					endDay: firstDayOfWeek - 1 || 0,
					timeCellStep: 240,
					timeLabelStep: 240,
					rowHeight: 'variable',
				},
			};
		}
	}
};

/**
 * Manages the active calendar view type via the URL query string (`?time=`).
 * Persists the selected view across page refreshes and allows deep-linking to a specific view.
 *
 * @param startOfWeek - Stringified day number for the first day of the week (from workplace settings). Defaults to `'0'` (Sunday).
 * @returns An object with:
 * - `calendarView` — the currently active view type.
 * - `calendarConfig` — the Mobiscroll view config for the active view.
 * - `setCalendarView(view)` — updates the active view in the URL.
 *
 * @example
 * const { calendarView, calendarConfig, setCalendarView } = useCalendarView('1');
 */
export const useCalendarView = (startOfWeek = '0') => {
	const { query, change } = useQueryParams();
	const { workplace: _workplace } = useWorkplacesContext();
	// console.log("here is workplace", workplace);

	const calendarView = (query.time || 'day') as CalendarView;

	const setCalendarView = (view: CalendarView) => {
		change({ time: view });
	};

	const calendarConfig = getCalendarConfig(
		calendarView,
		// getDayNumber(workplace.first_day_of_the_week)
		getDayNumber(+startOfWeek)
	);

	return { calendarView, calendarConfig, setCalendarView };
};
